import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Facebook, MessageCircle, Link2, Flag, ShieldCheck, CreditCard } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SafetyBanner, Spinner } from "@/components/common";

const SERIOUS_REASONS = ["Possible scam", "Suspicious payment", "Harassment", "Fake identity", "Prohibited content"];

export function ReportDialog({ open, onOpenChange, targetType, targetId, reportedUserId, reportedUserName, onBlocked }) {
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("form"); // form | block-prompt
  useEffect(() => {
    if (open) { api.get("/reports/reasons").then(({ data }) => setReasons(data.reasons)); setStep("form"); setReason(""); setDetails(""); }
  }, [open]);

  const submit = async () => {
    if (!reason) return toast.error("Please choose a reason");
    setLoading(true);
    try {
      await api.post("/reports", { target_type: targetType, target_id: targetId, reason, details });
      toast.success("Report submitted. Thank you for keeping Mtaa safe.");
      // Offer to block on serious reports — separate action, never automatic.
      if (reportedUserId && SERIOUS_REASONS.includes(reason)) {
        try {
          const { data } = await api.get(`/blocks/status/${reportedUserId}`);
          if (!data.blocked_by_me) { setStep("block-prompt"); setLoading(false); return; }
        } catch {}
      }
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  const doBlock = async () => {
    setLoading(true);
    try {
      await api.post("/blocks", { user_id: reportedUserId, reason, details });
      toast.success(`You blocked ${reportedUserName || "this user"}`);
      onBlocked?.();
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="report-dialog">
        {step === "form" ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Flag className="h-4 w-4 text-destructive" />Report this {targetType}</DialogTitle>
              <DialogDescription>Takes about 10 seconds. Our team reviews every report.</DialogDescription>
            </DialogHeader>
            <RadioGroup value={reason} onValueChange={setReason} className="grid grid-cols-2 gap-2">
              {reasons.map((r) => (
                <label key={r} className="flex cursor-pointer items-center gap-2 rounded-lg border border-border p-2 text-sm has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                  <RadioGroupItem value={r} data-testid={`report-reason-${r}`} /> {r}
                </label>
              ))}
            </RadioGroup>
            <Textarea placeholder="Add details (optional)" value={details} onChange={(e) => setDetails(e.target.value)} data-testid="report-details" />
            <DialogFooter>
              <Button onClick={submit} disabled={loading} data-testid="report-submit-btn" className="w-full">
                {loading ? <Spinner className="text-white" /> : "Submit report"}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" />Report submitted</DialogTitle>
              <DialogDescription data-testid="block-prompt-text">Would you also like to block {reportedUserName || "this user"}? They won't be able to message you and their listings will be hidden from you.</DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-row gap-2">
              <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} data-testid="report-block-notnow">Not now</Button>
              <Button variant="destructive" className="flex-1" onClick={doBlock} disabled={loading} data-testid="report-block-confirm">
                {loading ? <Spinner className="text-white" /> : "Block user"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ShareDialog({ open, onOpenChange, url, title }) {
  const full = url.startsWith("http") ? url : `${window.location.origin}${url}`;
  const share = (net) => {
    const enc = encodeURIComponent(full); const t = encodeURIComponent(title || "Check this out on Mtaa Australia");
    const links = { facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc}`, whatsapp: `https://wa.me/?text=${t}%20${enc}` };
    window.open(links[net], "_blank");
  };
  const copy = () => { navigator.clipboard.writeText(full); toast.success("Link copied"); };
  const native = () => { if (navigator.share) navigator.share({ title, url: full }); else copy(); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="share-dialog">
        <DialogHeader><DialogTitle>Share</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={() => share("facebook")} data-testid="share-facebook"><Facebook className="h-4 w-4" />Facebook</Button>
          <Button variant="outline" onClick={() => share("whatsapp")} data-testid="share-whatsapp"><MessageCircle className="h-4 w-4" />WhatsApp</Button>
          <Button variant="outline" onClick={copy} data-testid="share-copy"><Link2 className="h-4 w-4" />Copy link</Button>
          <Button variant="outline" onClick={native} data-testid="share-native">More…</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PhoneVerifyDialog({ open, onOpenChange, onVerified }) {
  const { refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("+61");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const send = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/phone/send", { phone });
      setStep(2);
      if (data.dev_mode) { setDevCode(data.dev_code); toast.info(`Demo mode: your code is ${data.dev_code}`); }
      else toast.success("Verification code sent");
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  const verify = async () => {
    setLoading(true);
    try {
      await api.post("/auth/phone/verify", { phone, code });
      toast.success("Phone verified 🎉");
      await refreshUser(); onVerified?.(); onOpenChange(false); setStep(1); setCode("");
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="phone-verify-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" />Verify your phone</DialogTitle>
          <DialogDescription>Required to sell, post a business or offer a service. Helps keep Mtaa high-trust.</DialogDescription>
        </DialogHeader>
        {step === 1 ? (
          <div className="space-y-3">
            <Label>Mobile number</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+61412345678" data-testid="phone-input" />
            <Button onClick={send} disabled={loading} className="w-full" data-testid="phone-send-btn">{loading ? <Spinner className="text-white" /> : "Send code"}</Button>
          </div>
        ) : (
          <div className="space-y-3">
            {devCode && <p className="rounded-lg bg-amber-50 p-2 text-center text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">Demo code: <b>{devCode}</b> (SMS not yet configured)</p>}
            <Label>Enter the 6-digit code</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" maxLength={6} data-testid="phone-code-input" />
            <Button onClick={verify} disabled={loading} className="w-full" data-testid="phone-verify-btn">{loading ? <Spinner className="text-white" /> : "Verify"}</Button>
            <button className="w-full text-sm text-muted-foreground hover:underline" onClick={() => setStep(1)}>Change number</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function CheckoutDialog({ open, onOpenChange, product, itemType, itemId }) {
  const [loading, setLoading] = useState(false);
  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: product.lookup_key, origin_url: window.location.origin,
        item_type: itemType, item_id: itemId });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setLoading(false); }
  };
  if (!product) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="checkout-dialog">
        <DialogHeader><DialogTitle>{product.name}</DialogTitle></DialogHeader>
        <div className="rounded-2xl border border-border bg-secondary/50 p-5 text-center">
          <p className="font-display text-4xl font-extrabold text-primary">${product.amount}</p>
          <p className="mt-1 text-sm text-muted-foreground">{product.description}</p>
        </div>
        <p className="text-center text-xs text-muted-foreground">Apple Pay, Google Pay & cards are shown at checkout when your device supports them.</p>
        <Button onClick={checkout} disabled={loading} className="w-full" size="lg" data-testid="checkout-pay-btn">
          {loading ? <Spinner className="text-white" /> : <><CreditCard className="h-4 w-4" />Continue to secure checkout</>}
        </Button>
        <SafetyBanner text="Payments are processed securely by Stripe. Mtaa never stores your card details." />
      </DialogContent>
    </Dialog>
  );
}
